import { computed } from "vue"
import { firestoreState } from "../stores/firestoreStore"

/**
 * Merchant auto-categorization: suggest a category for a new expense based on
 * what the user picked the last time they logged the same description.
 *
 * Descriptions are normalized (trimmed + lowercased) and matched exactly; the
 * suggestion is the categoryId of the most recently dated matching expense.
 * Only categories that still exist resolve to a suggestion, so a stale history
 * entry can never pre-select a deleted category.
 */
export function useCategorySuggestion() {
  // Resolve a stored category reference (historical rows hold either the
  // category id or its name) to the id of a currently existing category.
  const categoryIdIndex = computed(() => {
    const map = new Map<string, string>()
    for (const c of firestoreState.categories.value) {
      map.set(c.id, c.id)
      map.set(c.name, c.id)
    }
    return map
  })

  // normalized description → categoryId of the most recent matching expense.
  // Rebuilt only when the expenses/categories arrays change (one snapshot),
  // so repeated lookups while typing stay O(1).
  const suggestionIndex = computed(() => {
    const index = new Map<string, { categoryId: string; date: string }>()
    for (const expense of firestoreState.expenses.value) {
      if (expense.type !== "expense") continue
      const key = normalize(expense.description)
      if (!key) continue
      const categoryId = categoryIdIndex.value.get(expense.category)
      if (!categoryId) continue
      const existing = index.get(key)
      if (!existing || expense.date >= existing.date) {
        index.set(key, { categoryId, date: expense.date })
      }
    }
    return index
  })

  /**
   * Returns the suggested categoryId for a description, or null when there is
   * no history for it. Callers must only apply the suggestion when the user
   * has not already picked a category — never override an explicit choice.
   */
  function suggestCategoryId(description: string): string | null {
    const key = normalize(description)
    if (!key) return null
    return suggestionIndex.value.get(key)?.categoryId ?? null
  }

  return { suggestCategoryId }
}

function normalize(description: string | undefined | null): string {
  return (description ?? "").trim().toLowerCase()
}
